import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Check,
  ArrowUpDown,
  Loader2,
  Sparkles,
  Info,
  RefreshCw
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { classifyPopulate, locksTheControl } from '@/utils/populateOutcome';
import { describeSkips } from '@/utils/describeSkips';

interface BenefitAttributeMapping {
  id: string;
  sector_id: number;
  benefit_name: string;
  benefit_description: string;
  attribute_topic_id: string | null;
  attribute_name: string;
  attribute_topic_name: string | null;
  topic_description: string | null;
  importance_weight: number;
  direction: 'higher_better' | 'lower_better' | 'specific_values' | 'any';
  target_values: string[] | null;
  notes: string | null;
  created_at: string;
}

interface Benefit {
  benefit_name: string;
  benefit_description: string | null;
}

interface AttributeValueRange {
  id: string;
  attribute_name: string;
  product_category: string | null;
  data_type: string;
  min_value: number | null;
  max_value: number | null;
  typical_range_min: number | null;
  typical_range_max: number | null;
  unit: string | null;
  quality_tiers: Record<string, any> | null;
  canonical_values: string[] | null;
}

const directionIcons: Record<string, React.ReactNode> = {
  higher_better: <TrendingUp className="h-4 w-4" />,
  lower_better: <TrendingDown className="h-4 w-4" />,
  specific_values: <Target className="h-4 w-4" />,
  any: <Check className="h-4 w-4" />,
};

const directionColors: Record<string, string> = {
  higher_better: 'bg-green-100 text-green-800',
  lower_better: 'bg-blue-100 text-blue-800',
  specific_values: 'bg-purple-100 text-purple-800',
  any: 'bg-gray-100 text-gray-800',
};

const directionLabels: Record<string, string> = {
  higher_better: 'Higher is Better',
  lower_better: 'Lower is Better',
  specific_values: 'Specific Values',
  any: 'Any Value',
};

export default function BenefitAttributeMappings() {
  const { selectedSite } = useSite();
  const [mappings, setMappings] = useState<BenefitAttributeMapping[]>([]);
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [valueRanges, setValueRanges] = useState<AttributeValueRange[]>([]);
  const [selectedBenefit, setSelectedBenefit] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [populating, setPopulating] = useState(false);
  // ⇒ 'unknown' is a THIRD STATE and it is the whole point of this fix: "the request timed out at the
  // gateway" is neither a success nor a failure, and forcing it into either one is what told an
  // operator a 369-row write had failed. A BOOLEAN OUTCOME CANNOT CARRY "WE DO NOT KNOW".
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'unknown'; text: string; sector: number | null } | null>(null);
  // ⛔ THE LOCK IS SCOPED TO THE SECTOR THE UNKNOWN RUN BELONGED TO, not to the component.
  // It used to be a bare boolean, so after an ambiguous run on BikeInn, switching the header site
  // selector to RunnerInn loaded RunnerInn's data and left BikeInn's warning on screen with
  // RunnerInn's button dead. 🔑 THE LOCK WAS SCOPED TO THE COMPONENT AND THE FACT IS SCOPED TO THE
  // SECTOR — the same mismatch as the rest of this PR, one level up.
  // ⚠️ A plain reset-on-change would be worse: the operator switches away from a confusing message
  // and back, and the protection is gone. Recording WHICH sector is unresolved keeps the lock exactly
  // where the uncertainty is, and only there.
  // ⛔⛔ A SET, NOT A SCALAR. `unknownForSector` held ONE sector, so a second ambiguous run replaced
  // the first: 504 on sector 47, switch to 80, 504 there too, switch back to 47 — and 47's button was
  // live again because the state only remembered 80.
  // 🔑 THE FIX WENT boolean -> SINGLE SECTOR WHEN THE FACT NEEDED A SET. Each correction matched the
  // scope of the case in front of me rather than the scope of the fact, which is the same error this
  // whole PR has been about, now at its third scope. Unresolved sectors ACCUMULATE and are only
  // cleared by a reload — the one action that can actually resolve them.
  const [unresolvedSectors, setUnresolvedSectors] = useState<ReadonlySet<number>>(() => new Set());

  // ⛔⛔ NO `|| 1`. That was another default-as-assertion, two lines from the `?? 0` this PR spent four
  // rounds removing: a site with NO sector silently became sector 1.
  // ⇒ MEASURED ON db2 (production replica, read-only) 2026-08-09: of 43 sites, TWO have a NULL
  // sector — pagemind-dev and OutletInn — and sector 1 is a REAL, POPULATED sector belonging to
  // PageMind itself. So selecting OutletInn and pressing Auto-Populate posted to
  // /mappings/populate/1 and wrote benefit-attribute mappings and value ranges into PageMind's OWN
  // sector, silently, with the UI showing OutletInn throughout.
  // ⚠️ AND THE BUTTON'S GUARD DID NOT COVER IT: `disabled={... || !selectedSite}` guards on the site
  // EXISTING while the payload depends on the site HAVING A SECTOR. THE GUARD AND THE PAYLOAD WERE
  // DERIVED FROM DIFFERENT PROPERTIES OF THE SAME OBJECT, AND THEY COULD DISAGREE.
  const sectorId: number | null = selectedSite?.sector_id ?? null;
  const outcomeUnknown = sectorId !== null && unresolvedSectors.has(sectorId);

  useEffect(() => {
    if (sectorId !== null) {
      loadData();
    }
  }, [sectorId]);

  useEffect(() => {
    if (selectedBenefit && sectorId !== null) {
      loadMappings();
    }
  }, [selectedBenefit, sectorId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadBenefits(),
        loadMappings(),
        loadValueRanges(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      // Scoped to the sector it describes, like every other message on this page — otherwise a load
      // error for one site stays on screen after the operator switches to another.
      setMessage({
        type: 'error',
        text: 'Failed to load mapping data',
        sector: sectorId,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBenefits = async () => {
    try {
      const response = await authFetch(`${API_URL}/mappings/benefits/${sectorId}`);
      if (!response.ok) throw new Error('Failed to load benefits');
      const data = await response.json();
      setBenefits(data.benefits || []);
    } catch (error) {
      console.error('Error loading benefits:', error);
    }
  };

  const loadMappings = async () => {
    try {
      const url = selectedBenefit === 'all'
        ? `${API_URL}/mappings/benefit-attributes/${sectorId}`
        : `${API_URL}/mappings/benefit-attributes/${sectorId}?benefit=${encodeURIComponent(selectedBenefit)}`;

      const response = await authFetch(url);
      if (!response.ok) throw new Error('Failed to load mappings');
      const data = await response.json();
      setMappings(data.mappings || []);
    } catch (error) {
      console.error('Error loading mappings:', error);
    }
  };

  const loadValueRanges = async () => {
    try {
      const response = await authFetch(`${API_URL}/mappings/value-ranges/${sectorId}`);
      if (!response.ok) throw new Error('Failed to load value ranges');
      const data = await response.json();
      setValueRanges(data.ranges || []);
    } catch (error) {
      console.error('Error loading value ranges:', error);
    }
  };

  /**
   * ⛔⛔ EVERY BRANCH STATES WHAT IS *KNOWN* ABOUT THE WRITE. Three categories, not two:
   *
   *   DELIVERED + APPLIED   the server answered about work it did        -> success / real failure
   *   DELIVERED + REJECTED  the app said no (401, 4xx, 408)              -> real failure
   *   UNKNOWN               we never got an answer about work in flight  -> locked, do not re-press
   *
   * ⚠️ "UNKNOWN" IS DELIBERATELY THE SMALLEST CATEGORY, NOT THE DEFAULT. The first version of this
   * fix swept 408, every 502, a malformed response body and authFetch's 401 throw into "the server
   * may still be running" — which is simply false for all four, and would have left this screen
   * unable to report a real failure. That is the mirror defect, and it landed in the very change
   * written to avoid it: NAMING A DEFECT PROVIDES NO PROTECTION AGAINST IT.
   *   · 408 = the server timed out RECEIVING the request -> it did not run.
   *   · 502 = may never have reached the application at all -> unknown, but NOT "still running".
   *   · a 2xx with an unparseable body = delivery and execution are KNOWN; only the report is lost.
   *   · authFetch throws 'Unauthorized' on 401 = a CONFIRMED REJECTION, never an unknown write.
   * ⇒ Only an observed 504, a 502, or a genuine transport failure is UNKNOWN.
   */
  /**
   * ⛔⛔ ONE QUESTION, ASKED ONCE: MIGHT WRITES HAVE HAPPENED?
   *
   * Three previous revisions partitioned on the STATUS CODE and were corrected three times, each
   * time by moving a boundary inside the wrong partition. The two findings that survived were both
   * on 2xx — an unreadable body, and the backend's own `success:false, totalCreated>0` partial write
   * — so no move of that boundary could ever have reached them.
   * ⇒ Every path below now goes through `classifyPopulate`, and the control is LOCKED unless
   * non-execution is PROVEN. Enabled is the branch that must be earned.
   */
  const handleAutoPopulate = async () => {
    setPopulating(true);
    setMessage(null);
    const startedAt = Date.now();
    const elapsed = () => Math.round((Date.now() - startedAt) / 1000);

    /** Applies the outcome to BOTH the copy and the control, so they can never disagree again. */
    const apply = (text: string, type: 'success' | 'error' | 'unknown', locked: boolean) => {
      setMessage({ type, text, sector: sectorId });
      // A new Set each time: mutating in place would not re-render, and the lock would be invisible.
      if (locked && sectorId !== null) setUnresolvedSectors((prev) => new Set(prev).add(sectorId));
    };

    try {
      const response = await authFetch(`${API_URL}/mappings/populate/${sectorId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const raw = await response.text().catch(() => '');
        let serverSaid = '';
        try {
          const parsed = JSON.parse(raw);
          serverSaid = parsed?.error || parsed?.message || '';
        } catch {
          serverSaid = raw.slice(0, 300);
        }
        const detail = serverSaid ? ` (server said: ${serverSaid})` : '';
        const outcome = classifyPopulate({ ok: false, status: response.status });

        apply(
          outcome.kind === 'unknown' && outcome.stillRunning
            ? `The gateway timed out after ~${elapsed()}s. This job runs longer than the gateway will wait, so the `
              + `server is most likely STILL RUNNING it and still writing rows — the outcome is UNKNOWN. `
              + `Auto-Populate is disabled; reload this page in a minute to see what was written.${detail}`
            : `The request failed (HTTP ${response.status}) after ~${elapsed()}s. This job writes in several stages `
              + `WITHOUT a transaction, so rows may already have been written — the outcome is UNKNOWN. `
              + `Auto-Populate is disabled; reload to see the current state before deciding whether to retry.${detail}`,
          'unknown',
          locksTheControl(outcome)
        );
        // ⛔ Not reloading the tiles: a count rendered while a partial write may be in flight is the
        // third signal agreeing with a conclusion nobody can support.
        return;
      }

      // ⚠️ Parsed defensively: an unreadable 2xx is UNKNOWN, not "the work WAS performed". The
      // previous revision asserted the latter AND left the button live — leg 2's finding.
      // ⛔ STARTS FALSE AND IS EARNED BY A SUCCESSFUL PARSE. Initialising it to `true` would be the
      // same shape as the `?? 0` this round is fixing: a benign default standing in for a fact not
      // yet established. It happens to be overwritten in the catch today — but "correct because of
      // the control flow around it" is exactly how the other three defaults survived review.
      let data: any = null;
      let bodyReadable = false;
      try {
        data = await response.json();
        bodyReadable = true;
      } catch {
        bodyReadable = false;
      }

      const outcome = classifyPopulate({ ok: true, status: response.status, bodyReadable, body: data });

      if (outcome.kind === 'unknown') {
        // ⚠️ SAY WHAT WAS RECEIVED, NOT WHAT THE SERVER IS INFERRED TO HAVE DONE.
        // This previously read "The server accepted and ran the job" — but this branch also catches a
        // READABLE body that states nothing usable (`{}`, `{success:true}`), and a proxy can return
        // 2xx without the application having run at all. The control was already correct here; the
        // SENTENCE was over-claiming, which is the inverse of rounds 1-4 and the reason it is a minor
        // rather than a fifth blocker — but the operator acts on the sentence.
        // 🔑 It is the same discipline that made the 401 branch the only one to survive all four
        // rounds: it was grounded in what `authenticateJWT` DOES, not in what a status number implies.
        // ⇒ SEPARATE WORDING FOR THE TWO WAYS A 2xx CAN TELL US NOTHING. They are different facts and
        // the second one misleads precisely when an old or incompatible backend is involved.
        apply(
          `A successful HTTP response (${response.status}) was received, but `
          + (outcome.reason === 'unreadable'
            ? 'its body could not be parsed'
            : 'it did not report a write result in the expected form')
          + ` — so it is NOT known whether any rows were written. Auto-Populate is disabled; reload this page to see `
          + `the current state.`,
          'unknown',
          true
        );
        return;
      }

      const perStep = (data?.steps || [])
        .map((s: any) => `${s.step} ${s.created}${s.candidates != null ? `/${s.candidates}` : ''}`)
        .join(' · ');

      const skipDetail = describeSkips(data?.steps || []);

      if (outcome.kind === 'wrote') {
        // ⛔ INCLUDING the partial write: success:false with totalCreated>0 is the backend telling us
        // rows landed. Reporting that as a plain error and leaving the button live is what invited a
        // second expensive run over data that had already been written.
        apply(
          `${data?.message ?? ''}${perStep ? ` — ${perStep}` : ''}`
          + ` — ${outcome.totalCreated} row(s) were written${outcome.clean ? '' : ' despite errors'}. `
          + `Auto-Populate is disabled so the same work is not run twice; reload to continue.`
          + skipDetail,
          outcome.clean ? 'success' : 'error',
          true
        );
        await loadData();
        return;
      }

      // no-op: it ran and wrote nothing. The one 2xx that may re-enable, and only because the
      // backend asserts the count rather than us inferring it.
      // ⚠️ The text is derived from what the CLASSIFIER established (two explicit server assertions:
      // totalCreated === 0 AND wroteNothing === true), not substituted for a message the server did
      // not send. `data?.message ?? 'No mappings were written.'` would have asserted an outcome on
      // the server's behalf — the same one-token shape as `?? 0`, in a string.
      apply(
        `${data?.message ? `${data.message} ` : ''}The run completed and wrote no rows.${perStep ? ` — ${perStep}` : ''}`
        + skipDetail,
        'error',
        false
      );
      await loadData();
    } catch (error) {
      console.error('Error auto-populating:', error);
      const msg = error instanceof Error ? error.message : String(error);
      const outcome = classifyPopulate({ thrown: error });

      if (!locksTheControl(outcome)) {
        apply(
          outcome.kind === 'rejected' ? `${outcome.reason}.` : `${msg} — nothing was run.`,
          'error',
          false
        );
        return;
      }

      apply(
        `The request did not complete (${msg}), so it is not known whether the job started or what it wrote. `
        + `Auto-Populate is disabled; reload this page to check the current state.`,
        'unknown',
        true
      );
    } finally {
      setPopulating(false);
    }
  };

  const getValueRangeForAttribute = (attributeName: string): AttributeValueRange | undefined => {
    return valueRanges.find(r => r.attribute_name === attributeName);
  };

  const formatWeightPercentage = (weight: number): string => {
    return `${Math.round(weight * 100)}%`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Benefit-Attribute Mappings</h1>
          <p className="text-muted-foreground">
            View and manage how product attributes map to customer benefits
          </p>
        </div>
        {/* ⛔⛔ `outcomeUnknown` DISABLES THE CONTROL, not just the copy. The previous revision told the
            operator "DO NOT press Auto-Populate again" and left the button enabled — the text said do
            not and the control said you may, and THE CONTROL WINS EVERY TIME. The acceptance test for
            this is "the button is disabled after an unknown outcome", never "the message appears": a
            test on the copy passed while the duplicate job was still one click away. */}
        <Button
          onClick={handleAutoPopulate}
          disabled={populating || !selectedSite || sectorId === null || outcomeUnknown}
          title={
            outcomeUnknown
              ? "Disabled: the previous run's outcome for this sector is unknown. Reload the page to re-enable."
              : sectorId === null
                ? 'Disabled: this site has no sector, so there is no sector to populate.'
                : undefined
          }
          className="gap-2"
        >
          {populating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Auto-Populating...
            </>
          ) : outcomeUnknown ? (
            <>
              <Info className="h-4 w-4" />
              Reload to re-enable
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Auto-Populate with AI
            </>
          )}
        </Button>
      </div>

      {/* Message Alert.
          ⛔ 'unknown' is deliberately NOT destructive. Red is precisely the styling that told the
          operator the run had failed and invited a second press of an expensive job. Only a real
          application failure earns the destructive variant. */}
      {message && message.sector === sectorId && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          {/* ⛔ whitespace-pre-wrap: describeSkips emits newlines and indentation, and normal
              HTML whitespace collapses every one of them into a single paragraph — twenty
              sampled skips became one unreadable line. Formatting nobody rendered is
              formatting nobody checked. */}
          <AlertDescription className="whitespace-pre-wrap">{message.text}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Mappings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mappings.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Unique Benefits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{benefits.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Value Ranges Defined</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{valueRanges.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter mappings by benefit type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Select value={selectedBenefit} onValueChange={setSelectedBenefit}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a benefit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Benefits</SelectItem>
                  {benefits.map((benefit) => (
                    <SelectItem key={benefit.benefit_name} value={benefit.benefit_name}>
                      {benefit.benefit_name}
                      {benefit.benefit_description && (
                        <span className="text-muted-foreground ml-2">
                          - {benefit.benefit_description}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={loadData}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Mappings Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedBenefit === 'all'
              ? 'All Benefit-Attribute Mappings'
              : `"${selectedBenefit}" Mappings`
            }
          </CardTitle>
          <CardDescription>
            {mappings.length} mapping{mappings.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No mappings found</p>
              <p className="text-sm mt-2">
                Try auto-populating with AI to generate mappings
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benefit</TableHead>
                    <TableHead>Attribute</TableHead>
                    <TableHead>Weight</TableHead>
                    <TableHead>Direction</TableHead>
                    <TableHead>Target Values</TableHead>
                    <TableHead>Value Range</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.map((mapping) => {
                    const valueRange = getValueRangeForAttribute(mapping.attribute_name);
                    return (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{mapping.benefit_name}</div>
                            {mapping.benefit_description && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {mapping.benefit_description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{mapping.attribute_name}</div>
                            {mapping.attribute_topic_name && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Topic: {mapping.attribute_topic_name}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {formatWeightPercentage(mapping.importance_weight)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={directionColors[mapping.direction]}>
                            <span className="flex items-center gap-1">
                              {directionIcons[mapping.direction]}
                              {directionLabels[mapping.direction]}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {mapping.target_values && mapping.target_values.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {mapping.target_values.map((value, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {value}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {valueRange ? (
                            <div className="text-xs">
                              {valueRange.min_value !== null && valueRange.max_value !== null ? (
                                <div>
                                  {valueRange.min_value} - {valueRange.max_value}
                                  {valueRange.unit && ` ${valueRange.unit}`}
                                </div>
                              ) : (
                                <div className="text-muted-foreground">No range</div>
                              )}
                              {valueRange.canonical_values && valueRange.canonical_values.length > 0 && (
                                <div className="text-muted-foreground mt-1">
                                  Values: {valueRange.canonical_values.slice(0, 3).join(', ')}
                                  {valueRange.canonical_values.length > 3 && '...'}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapping.notes ? (
                            <div className="text-xs text-muted-foreground max-w-xs truncate">
                              {mapping.notes}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
